import { SelectorStatus } from '../../components/CampaignAndGroupSelector/CampaignAndGroupSelector';
import { getColumnsSettings } from '../../declarations/common';
import { AdditionalFiltersInStorageHelpers } from '../../service/AdditionalFiltersInStorageHelpers';
import { getDefaultBannerPriceData } from '../../service/BannerHelpers/BannerHelpers';
import { getGridInitialData } from '../../service/GridHelpers/GridHelpers';
import { GridStorageHelper } from '../../service/GridStorageHelper/GridStorageHelper';
import {
    BannersAdditionalFilter,
    CampaignsAdditionalFilter,
    CampaignsCalcTypeFilter,
    CopyTooltipStatus,
    GroupsAdditionalFilter
} from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    defaultClientInfo,
    defaultRowsCount,
    getEmptyGridSelectionInfo,
    getEmptySearchAndReplaceInspectorState,
    GridInitialData,
    GridSelectionInfo
} from '../../typings/grid';
import {
    DuplicateGroupsModalViewState,
    DuplicateRadioControlType
} from '../../typings/groups';
import { PricesWizardType } from '../../typings/inspector';
import { GridState, State } from '../../typings/state';

const pricesWizardDefaults = {
    volume: 100,
    coverage: 100,
    increase: '30',
    price: ''
};

export function getGridInitialState(data: GridInitialData): GridState {
    return {
        gridData: {
            total: defaultRowsCount,
            allItemsIds: [],
            data: [],
            totalStats: undefined
        },
        gridReqId: '',
        textSuggestItems: [],
        isSuggestItemsRequested: false,
        hasObjectsOverLimit: false,
        isActionInProgress: false,
        selectionInfo: getEmptyGridSelectionInfo(),
        clientInfo: defaultClientInfo,
        columnsSettings: getColumnsSettings(),
        isHeaderVisible: true,
        scrollPosition: 0,
        isHorizontalScrollVisible: true,
        isGridScrolling: false,
        editCellsData: {},
        tooltips: {},
        searchAndReplaceInspectorState: getEmptySearchAndReplaceInspectorState(),
        bannersPreviewData: {},
        inspector: {
            pricesWizardOpened: false
        },
        pricesWizardData: {
            isSaveInProgress: false,
            wizardType: PricesWizardType.simple,
            contextData: undefined,
            error: undefined,
            simple: {
                search: {
                    price: pricesWizardDefaults.price,
                    checked: true
                },
                net: {
                    price: pricesWizardDefaults.price,
                    checked: true
                }
            },
            extended: {
                search: {
                    volume: pricesWizardDefaults.volume,
                    increasePercentage: pricesWizardDefaults.increase,
                    priceLimit: pricesWizardDefaults.price,
                    checked: true
                },
                net: {
                    coverage: pricesWizardDefaults.coverage,
                    increasePercentage: pricesWizardDefaults.increase,
                    priceLimit: pricesWizardDefaults.price,
                    checked: true
                },
                mcbanner: {
                    increasePercentage: pricesWizardDefaults.increase,
                    priceLimit: pricesWizardDefaults.price,
                    checked: true
                }
            }
        },
        additionalFilterDict: AdditionalFiltersInStorageHelpers.getFilter({
            campaigns: CampaignsAdditionalFilter.ALL_ACTIVE,
            banners: BannersAdditionalFilter.ALL_ACTIVE,
            groups: GroupsAdditionalFilter.ALL_ACTIVE
        }),
        campaignsCalcTypeFilter:
            data.campaignsCalcTypeFilter || CampaignsCalcTypeFilter.CPC,
        isGridTotalBoxVisible: GridStorageHelper.getGridTotalBoxVisibility(),
        isOnlyFavoriteCampaignsVisible: GridStorageHelper.getOnlyFavoriteCampaignsSetting(),
        isCompactViewEnabled: GridStorageHelper.getCompactViewEnabled(),
        bannersPricesPopup: {
            isVisible: false,
            isPriceFormVisible: true,
            isOnProgress: true,
            errors: [],
            bannerPrice: getDefaultBannerPriceData(CurrencyCode.RUB)
        },
        filterDict: data.filterDict,
        campaignAndGroupSelector: {
            selectorStatus: SelectorStatus.LOADING_CAMPAIGN,
            isCampaignAndGroupSelectorOpen: false
        },
        phraseAdjustment: {
            visible: false,
            showsForecast: undefined,
            hasMoreItems: false,
            items: undefined
        },
        minusPhrasesSelectorData: {
            groupsIds: [],
            isMinusPhraseSelectorModalOpened: false,
            selectedMinusPhrasesIds: []
        },
        isFilterParamsChanged: false,
        goalsSettings: data.goalsSettings,
        duplicateGroupsData: {
            selectedRadioButtonType: DuplicateRadioControlType.ACTUAL_CAMPAIGN,
            items: [],
            campaignsSelector: {
                error: false,
                isLoading: false
            },
            isModalOpen: false,
            modalViewState: DuplicateGroupsModalViewState.duplicate,
            validationResults: {
                copyErrorsBrokenGroupsIdxList: [],
                copyErrorLimitIdxList: []
            }
        },
        retargetingRulesInfo: {
            copyTooltipStatus: CopyTooltipStatus.hidden
        },
        feedFilters: {
            isRateModalOpen: false,
            isPriorityModalOpen: false
        }
    };
}

export function resetGridDataReducer(state: GridState): GridState {
    return {
        ...state,
        gridData: {
            total: defaultRowsCount,
            allItemsIds: [],
            data: [],
            totalStats: state.isFilterParamsChanged
                ? undefined
                : state.gridData.totalStats
        },
        scrollPosition: 0
    };
}

export function resetGridStateReducer(
    state: GridState,
    fullState: State,
    selectionInfo: GridSelectionInfo = getEmptyGridSelectionInfo()
): GridState {
    const gridInitialData = getGridInitialData(fullState.commonData.login);

    return {
        ...getGridInitialState(gridInitialData),
        selectionInfo,
        columnsSettings: getColumnsSettings(),
        filterDict: state.filterDict,
        isCompactViewEnabled: state.isCompactViewEnabled,
        isOnlyFavoriteCampaignsVisible: state.isOnlyFavoriteCampaignsVisible,
        clientInfo: state.clientInfo
    };
}
